import { Box, DialogActions, DialogContent, Tab, Tabs } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionButton, Dialog, HasRole, IsSuperAdmin, Pagination } from 'app/components';
import { useDialog, useReportsGetDataFilterQuery, useSnakeBar, useUserUpdateMutation } from 'app/hooks';
import { StoreSelectors, useStoreSelector } from 'app/store';
import { TranslationKeys } from 'app/translations';
import type { UseQueryRefetch, User } from 'app/types';

import { UserAccessesListDataFilter } from './components';
import { UserAccessesListConfirmDialog } from './components/UserAccessesListConfirmDialog/UserAccessesListConfirmDialog';
import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from './UserAccessesList.types';
import { UserEnvironmentsList } from '../UserEnvironmentsList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = React.memo(function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
});

type UserAccessesListProps = {
  user: User | undefined;
  isLoggedInUser?: boolean;
  openConfirmDeleteDialog: () => void;
  refetchGetUserQuery: UseQueryRefetch;
  onRequestAPIKey: () => void;
};

export const UserAccessesList = ({
  user,
  openConfirmDeleteDialog,
  refetchGetUserQuery,
  isLoggedInUser = false,
  onRequestAPIKey,
}: UserAccessesListProps) => {
  const { mutate: updateUserMutation, isLoading: isLoadingUserUpdateMutation } = useUserUpdateMutation();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const {
    dialogState: showConfirmDialog,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();

  const isSuperAdmin = IsSuperAdmin();
  const [userDefaultSelectedData, setUserDefaultSelectedData] = React.useState<UserAccessesListSelectedData>();
  const [selectedData, setSelectedData] = React.useState<UserAccessesListSelectedData>();
  const [hasEventFlowChanges, setHasEventFlowChanges] = React.useState(false);
  const [confirmationDialog, setConfirmationDialog] = React.useState(false);
  const loggedInUserId = useStoreSelector<string>(state => StoreSelectors.AppSelector.selectUserId(state.AppReducer));
  const environmentOwnerId = useStoreSelector<string>(state => state.AppReducer.environment.ownerId);
  const hasAPIRole = HasRole('ROLE_API_API') && loggedInUserId === user?.userId;

  React.useEffect(() => {
    setUserDefaultSelectedData(undefined);
    setSelectedData(undefined);
    setHasEventFlowChanges(false);
  }, [user?.userId]);

  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(25);
  const [searchQuery, setSearchQuery] = React.useState('');

  const {
    data: dataFilter,
    isLoading: isLoadingDataFilterQuery,
    isFetching: isFetchingDataFilterQuery,
    refetch: refetchDataFilterQuery,
  } = useReportsGetDataFilterQuery({
    variables: {
      userId: user?.userId,
      page,
      limit,
      searchQuery: searchQuery || undefined,
    },
    options: {
      enabled: !!user?.userId,
    },
  });

  React.useEffect(() => {
    if (!user) return;
    if (isLoadingDataFilterQuery) return;
    if (userDefaultSelectedData) return;

    const environmentAccess = user.accesses?.[environmentOwnerId];
    const accesses: UserAccessesListSelectedData = {
      allEmployers: environmentAccess?.allEmployers || false,
      employerIds: environmentAccess?.employerIds || [],
      eventFlows: [
        ...(dataFilter?.results?.flatMap(result => {
          const employers = 'subOwnerId' in result ? result.employers : [result];
          return employers.flatMap(employer => [
            ...(employer.eventFlowDefaultUser === user.userId ? [employer.employerId] : []),
            ...(employer.subEmployers
              ?.filter(({ eventFlowDefaultUser }) => eventFlowDefaultUser === user.userId)
              .map(subEmployer => subEmployer.employerId) || []),
          ]);
        }) || []),
      ],
      autoAssignEmployers: user.autoAssignEmployers || false,
    };

    const defaultAccesses = JSON.parse(JSON.stringify(accesses));

    setSelectedData(accesses);
    setUserDefaultSelectedData(defaultAccesses);
    setHasEventFlowChanges(false);
  }, [user, dataFilter, environmentOwnerId, isLoadingDataFilterQuery, userDefaultSelectedData]);

  const hasChangedAccesses = React.useMemo(() => {
    const { eventFlows: defaultEventFlows, ...defaultRest } = userDefaultSelectedData || {};
    const { eventFlows: currentEventFlows, ...currentRest } = selectedData || {};

    const hasChangedRest = JSON.stringify(defaultRest) !== JSON.stringify(currentRest);
    // compare as sets so re-ordering alone never masks (or fakes) a real change
    const hasChangedEventFlows =
      JSON.stringify([...(defaultEventFlows || [])].sort((a, b) => a.localeCompare(b))) !==
      JSON.stringify([...(currentEventFlows || [])].sort((a, b) => a.localeCompare(b)));

    return hasChangedRest || hasChangedEventFlows || hasEventFlowChanges;
  }, [hasEventFlowChanges, userDefaultSelectedData, selectedData]);

  const onClickCancelAccessesChanges = React.useCallback(() => {
    setSelectedData(userDefaultSelectedData);
    setHasEventFlowChanges(false);
  }, [userDefaultSelectedData]);

  const onChangeSelected = React.useCallback<UserAccessesListOnChangeSelected>(
    newSelectedData => {
      if (JSON.stringify(selectedData?.eventFlows || []) !== JSON.stringify(newSelectedData.eventFlows || [])) {
        setHasEventFlowChanges(true);
      }
      setSelectedData(newSelectedData);
    },
    [selectedData?.eventFlows],
  );

  const updateUserAccesses = React.useCallback(() => {
    if (user && selectedData) {
      const method = 'updateUser';

      updateUserMutation(
        {
          userId: user.userId,
          ownerId: user.ownerId || null,
          roleId: user.roleId,
          firstName: user.personName.firstName,
          middleName: user.personName.middleName,
          lastName: user.personName.lastName,
          emailAddress: user.emailAddress,
          language: user.language,
          internal: user.internal,
          accesses: environmentOwnerId
            ? {
                ...user.accesses,
                [environmentOwnerId]: {
                  allEmployers: selectedData.allEmployers,
                  employerIds: selectedData.allEmployers ? [] : selectedData.employerIds,
                },
              }
            : user.accesses,
          eventFlows: selectedData.eventFlows,
          autoAssignEmployers: selectedData.autoAssignEmployers,
        },
        {
          onSuccess: () => {
            closeConfirmDialog();
            refetchGetUserQuery();
            refetchDataFilterQuery();
            showSuccessSnakeBar({ method });
          },
          onError: (error: any) => {
            showErrorSnakeBar({ method, message: error?.message });
          },
        },
      );
    }
  }, [
    closeConfirmDialog,
    refetchDataFilterQuery,
    refetchGetUserQuery,
    selectedData,
    showErrorSnakeBar,
    showSuccessSnakeBar,
    updateUserMutation,
    environmentOwnerId,
    user,
  ]);

  const isDataFilterQueryLoading = isLoadingDataFilterQuery || isFetchingDataFilterQuery;
  const isLoading = isDataFilterQueryLoading || isLoadingUserUpdateMutation;

  const [tabValue, setValue] = React.useState(0);

  const handleChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  }, []);

  const onPaginationChange = React.useCallback(({ page: newPage, rowSize }: { page?: number; rowSize?: number }) => {
    if (newPage !== undefined) setPage(newPage);
    if (rowSize !== undefined) {
      setLimit(rowSize);
      setPage(1);
    }
  }, []);

  const onSearchQueryChange = React.useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleChange} aria-label="basic tabs example">
          <Tab label="Employers and Subowners" id={'simple-tab-1'} />
          {isSuperAdmin && <Tab label="Environments" id={'simple-tab-2'} />}
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={'h6'}>
            <FormattedMessage id={TranslationKeys.users_accessesList_headerText} />
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            {hasAPIRole && (
              <ActionButton
                variant={'outlined'}
                onClick={onRequestAPIKey}
                disabled={isLoading}
                messageId={TranslationKeys.button_requestAPI}
              />
            )}
            <ActionButton
              variant={'outlined'}
              messageId={TranslationKeys.button_delete}
              onClick={openConfirmDeleteDialog}
              disabled={isLoggedInUser || isLoading}
            />
          </Box>
        </Box>
        <UserAccessesListDataFilter
          isLoading={isDataFilterQueryLoading || isLoadingUserUpdateMutation}
          isLoggedInUser={isLoggedInUser}
          onChangeSelected={onChangeSelected}
          selectedData={selectedData}
          results={dataFilter?.results}
          selectedEventFlows={selectedData?.eventFlows || []}
          openConfirmationDialog={setConfirmationDialog}
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
        />

        {(dataFilter?.totalPages ?? 0) > 0 && (
          <Pagination
            count={dataFilter?.totalItems}
            page={dataFilter?.page ?? page}
            rowsPerPage={dataFilter?.limit ?? limit}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPaginationChange={onPaginationChange}
          />
        )}

        <Box marginTop={3} marginBottom={3} display={'flex'} justifyContent={'end'} alignItems={'center'} gap={2}>
          <ActionButton
            variant={'outlined'}
            messageId={TranslationKeys.button_cancel}
            onClick={onClickCancelAccessesChanges}
            disabled={isLoggedInUser || isLoading || !hasChangedAccesses}
          />
          <ActionButton
            messageId={TranslationKeys.button_save}
            onClick={openConfirmDialog}
            disabled={isLoggedInUser || isLoading}
          />
        </Box>

        {showConfirmDialog && !!selectedData && (
          <UserAccessesListConfirmDialog
            show={showConfirmDialog}
            isLoading={isLoading}
            onClose={closeConfirmDialog}
            onConfirm={updateUserAccesses}
            selectedData={selectedData}
            results={dataFilter?.results}
          />
        )}
        <Dialog
          open={confirmationDialog}
          title={<FormattedMessage id={TranslationKeys.users_accessesList_dialog_title} />}
          onClose={() => {
            setConfirmationDialog(false);
          }}
        >
          <DialogContent>
            <Typography variant={'body1'}>
              <FormattedMessage id={TranslationKeys.users_accessesList_dialog_content} />
            </Typography>
          </DialogContent>
          <DialogActions>
            <ActionButton
              messageId={TranslationKeys.button_confirm}
              onClick={() => {
                setConfirmationDialog(false);
              }}
            />
          </DialogActions>
        </Dialog>
      </CustomTabPanel>
      {isSuperAdmin && (
        <CustomTabPanel value={tabValue} index={1}>
          <UserEnvironmentsList user={user} refetchGetUserQuery={refetchGetUserQuery}></UserEnvironmentsList>
        </CustomTabPanel>
      )}
    </>
  );
};
